// CLI output helpers (ARCHITECTURE.md §14.2).
//
// Centralizes the three output formats (json | yaml | text) and the
// Map→plain-object conversions the graph types require before serialization.
// The DependencyGraph (and its embedded AstGraph) use native Maps, which are
// not JSON-serializable, so they are flattened here before emission.

import { stringify as yamlStringify } from "yaml";

import { serializeAstGraph } from "../ast/types.js";
import type { DependencyGraph } from "../dependency/types.js";

// `dot` is a Graphviz output format used by the `callgraph` command. Commands
// that have no graph rendering treat it as `text` (see writeOutput), so it is a
// safe global value.
export const OUTPUT_FORMATS = ["json", "yaml", "text", "dot"] as const;

export type OutputFormat = (typeof OUTPUT_FORMATS)[number];

/** A sink for CLI output; defaults to stdout. Overridable for tests. */
export type Writer = (line: string) => void;

const defaultWriter: Writer = (line) => process.stdout.write(`${line}\n`);

/**
 * Emit `data` in the requested format. For `json`/`yaml` the structured `data`
 * is serialized; for `text` the `text` renderer produces a human summary.
 */
export function writeOutput(options: {
  format: OutputFormat;
  data: unknown;
  text: () => string;
  writer?: Writer;
}): void {
  const write = options.writer ?? defaultWriter;
  switch (options.format) {
    case "json":
      write(JSON.stringify(options.data, null, 2));
      return;
    case "yaml":
      write(yamlStringify(options.data));
      return;
    case "text":
    // `dot` is graph-only; for commands without a graph rendering it degrades
    // to the human-readable text summary.
    case "dot":
      write(options.text());
      return;
  }
}

/**
 * Flatten a DependencyGraph into a JSON/YAML-safe object. The embedded AST
 * graph is flattened via `serializeAstGraph`; `nodes` and `artifactMapping`
 * Maps become plain records.
 */
export function serializeDependencyGraph(
  graph: DependencyGraph,
): Record<string, unknown> {
  return {
    ast: serializeAstGraph(graph.ast),
    nodes: Object.fromEntries(graph.nodes),
    edges: graph.edges,
    artifactMapping: Object.fromEntries(graph.artifactMapping),
  };
}
